import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { AppAccessGate, ClientProviders } from "@/rentos/ui";
import { PlatformAdminShell } from "../app/_components/PlatformAdminShell";
import { ErrorBoundary } from "@/common/components/ErrorBoundary";

// Eagerly loaded (auth + shell)
import HomePage from "../app/page";
import SignInPage from "../app/signin/page";
import ForgotPasswordPage from "../app/forgot-password/page";
import SetPasswordPage from "../app/set-password/page";
import NotFoundPage from "../app/not-found";

// Lazy-loaded admin pages
const AdminDashboardPage = lazy(() => import("../app/admin/page"));
const AdminAnalyticsPage = lazy(() => import("../app/admin/analytics/page"));
const AdminBillingPage = lazy(() => import("../app/platform/billing/page"));
const AdminBusinessesPage = lazy(() => import("../app/admin/businesses/page"));
const AdminBusinessDetailPage = lazy(() => import("../app/admin/businesses/[id]/page"));
const AdminBusinessEditPage = lazy(() => import("../app/admin/businesses/[id]/edit/page"));
const AdminBusinessCreatePage = lazy(() => import("../app/admin/businesses/create/page"));
const AdminCmsPage = lazy(() => import("../app/admin/cms/page"));
const AdminCmsPostCreatePage = lazy(() => import("../app/admin/cms/posts/create/page"));
const AdminCmsPostEditPage = lazy(() => import("../app/admin/cms/posts/[id]/edit/page"));
const AdminLogsPage = lazy(() => import("../app/admin/logs/page"));
const AdminNotificationsPage = lazy(() => import("../app/admin/notifications/page"));
const AdminNotificationCreatePage = lazy(() => import("../app/admin/notifications/create/page"));
const AdminPlansPage = lazy(() => import("../app/admin/plans/page"));
const AdminProfilePage = lazy(() => import("../app/admin/profile/page"));
const AdminSecurityPage = lazy(() => import("../app/admin/security/page"));
const AdminSettingsPage = lazy(() => import("../app/admin/settings/page"));
const AdminSettingsEmailPage = lazy(() => import("../app/admin/settings/email/page"));
const AdminSettingsGeneralPage = lazy(() => import("../app/admin/settings/general/page"));
const AdminSettingsSecurityPage = lazy(() => import("../app/admin/settings/security/page"));
const AdminSupportPage = lazy(() => import("../app/admin/support/page"));
const AdminSupportDetailPage = lazy(() => import("../app/admin/support/[id]/page"));
const AdminTestimonialsPage = lazy(() => import("../app/admin/testimonials/page"));
const AdminTestimonialReviewPage = lazy(() => import("../app/admin/testimonials/[id]/review/page"));
const AdminLegalPagesPage = lazy(() => import("../app/admin/legal-pages/page"));
const AdminLegalPageCreatePage = lazy(() => import("../app/admin/legal-pages/create/page"));
const AdminLegalPageEditPage = lazy(() => import("../app/admin/legal-pages/[id]/edit/page"));
const AdminAllUsersPage = lazy(() => import("../app/admin/users/page"));
const AdminRoleManagementPage = lazy(() => import("../app/admin/role-management/page"));
const AdminHealthPage = lazy(() => import("../app/admin/health/page"));
const EmailTemplatesPage = lazy(() => import("../app/platform/email-templates/page"));
const FinancePage = lazy(() => import("../app/platform/finance/page"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export default function App() {
  return (
    <>
      <ClientProviders appScope="platform-admin">
        <PlatformAdminShell>
          <AppAccessGate appScope="platform-admin">
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/set-password" element={<SetPasswordPage />} />

                  {/* Admin routes */}
                  <Route path="/console" element={<AdminDashboardPage />} />
                  <Route path="/console/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="/console/billing" element={<AdminBillingPage />} />
                  <Route path="/console/businesses" element={<AdminBusinessesPage />} />
                  <Route path="/console/businesses/create" element={<AdminBusinessCreatePage />} />
                  <Route path="/console/businesses/:id" element={<AdminBusinessDetailPage />} />
                  <Route path="/console/businesses/:id/edit" element={<AdminBusinessEditPage />} />
                  <Route path="/console/cms" element={<AdminCmsPage />} />
                  <Route path="/console/cms/posts/create" element={<AdminCmsPostCreatePage />} />
                  <Route path="/console/cms/posts/:id/edit" element={<AdminCmsPostEditPage />} />
                  <Route path="/console/logs" element={<AdminLogsPage />} />
                  <Route path="/console/notifications" element={<AdminNotificationsPage />} />
                  <Route path="/console/notifications/create" element={<AdminNotificationCreatePage />} />
                  <Route path="/console/plans" element={<AdminPlansPage />} />
                  <Route path="/console/profile" element={<AdminProfilePage />} />
                  <Route path="/console/security" element={<AdminSecurityPage />} />
                  <Route path="/console/settings" element={<AdminSettingsPage />} />
                  <Route path="/console/settings/email" element={<AdminSettingsEmailPage />} />
                  <Route path="/console/settings/general" element={<AdminSettingsGeneralPage />} />
                  <Route path="/console/settings/security" element={<AdminSettingsSecurityPage />} />
                  <Route path="/console/support" element={<AdminSupportPage />} />
                  <Route path="/console/support/:id" element={<AdminSupportDetailPage />} />
                  <Route path="/console/testimonials" element={<AdminTestimonialsPage />} />
                  <Route path="/console/testimonials/:id/review" element={<AdminTestimonialReviewPage />} />
                  <Route path="/console/legal-pages" element={<AdminLegalPagesPage />} />
                  <Route path="/console/legal-pages/create" element={<AdminLegalPageCreatePage />} />
                  <Route path="/console/legal-pages/:id/edit" element={<AdminLegalPageEditPage />} />
                  <Route path="/console/users" element={<AdminAllUsersPage />} />
                  <Route path="/console/role-management" element={<AdminRoleManagementPage />} />
                  <Route path="/console/health" element={<AdminHealthPage />} />
                  <Route path="/console/email-templates" element={<EmailTemplatesPage />} />
                  <Route path="/console/finance" element={<FinancePage />} />

                  {/* Catch-all */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </AppAccessGate>
        </PlatformAdminShell>
      </ClientProviders>
    </>
  );
}
